import {
  CIVILITY_LABEL,
  CONTACT_STATUS_LABEL,
  ContactStatus,
  WEALTH_SOURCE_LABEL,
  WEALTH_TRANCHE_LABEL,
} from '@crm/shared';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ContactFormModal } from '../components/contacts/ContactFormModal';
import { NewOpportunityModal } from '../components/contacts/NewOpportunityModal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { TrashIcon } from '../components/ui/icons';
import type { ContactRow } from '../lib/api';
import { apiClient } from '../lib/api';
import { formatChf } from '../lib/format';

const STAGE_LABEL: Record<string, string> = {
  identified: 'Identified',
  qualifying: 'Qualifying',
  compliance_review: 'Compliance review',
  proposal: 'Proposal',
  onboarding: 'Onboarding',
  active: 'Active',
};

const STATUS_TONE: Record<ContactStatus, string> = {
  prospect: 'bg-platinum/60 text-ink-secondary',
  qualified_lead: 'bg-glacier text-graphite border border-graphite/20',
  client: 'bg-bg-success text-semantic-success',
};

export function ContactDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<ContactRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'info' | 'opportunities' | 'notes'>('info');
  const [newOpp, setNewOpp] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    apiClient
      .getContact(id)
      .then(setContact)
      .catch((e) => setError((e as Error).message));
  }, [id]);

  async function performDelete() {
    if (!contact) return;
    setDeleting(true);
    try {
      await apiClient.deleteContact(contact.id);
      navigate('/contacts');
    } catch (e) {
      setDeleting(false);
      setConfirmOpen(false);
      setError((e as Error).message);
    }
  }

  if (error) {
    return (
      <div className="px-8 py-10 max-w-content mx-auto">
        <p className="text-semantic-error">Error: {error}</p>
        <Link to="/contacts" className="text-sm underline text-ink-secondary">
          ← Back
        </Link>
      </div>
    );
  }
  if (!contact) {
    return (
      <div className="px-8 py-10 max-w-content mx-auto">
        <p className="text-sm text-ink-tertiary">Loading…</p>
      </div>
    );
  }

  return (
    <div className="px-8 py-10 max-w-content mx-auto">
      <Link
        to="/contacts"
        className="text-xs uppercase tracking-wider text-ink-tertiary hover:text-ink"
      >
        ← Directory
      </Link>

      <header className="mt-6 flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-full bg-platinum overflow-hidden flex items-center justify-center text-graphite font-display text-xl font-semibold">
            {contact.photo_url ? (
              <img src={contact.photo_url} alt="" className="w-full h-full object-cover" />
            ) : (
              `${contact.first_name[0] ?? ''}${contact.last_name[0] ?? ''}`.toUpperCase()
            )}
          </div>
          <div>
            <p className="label-uppercase">— Contact</p>
            <h1 className="mt-1 font-display text-3xl text-graphite font-semibold">
              {contact.civility ? `${CIVILITY_LABEL[contact.civility]} ` : ''}
              {contact.first_name} {contact.last_name}
            </h1>
            <p className="mt-1 text-sm text-ink-secondary">
              {contact.company ? (
                <>
                  <span>{contact.company}</span>
                  {contact.position && (
                    <span className="text-ink-tertiary"> · {contact.position}</span>
                  )}
                </>
              ) : (
                <span className="text-ink-tertiary">Individual</span>
              )}
            </p>
            <div className="mt-3 flex items-center gap-3">
              <span
                className={
                  'inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full ' +
                  STATUS_TONE[contact.status]
                }
              >
                {CONTACT_STATUS_LABEL[contact.status]}
              </span>
              {contact.pep_flag && (
                <span className="text-[10px] uppercase tracking-wider text-signal">PEP</span>
              )}
              {contact.owner_rm && (
                <span className="text-xs text-ink-tertiary">
                  RM · {contact.owner_rm.display_name}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setEditing(true)} className="btn-secondary">
            Edit
          </button>
          <button type="button" onClick={() => setNewOpp(true)} className="btn-primary">
            + New opportunity
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={deleting}
            className="btn-icon-danger disabled:opacity-50"
            aria-label="Delete contact"
            title="Delete contact"
          >
            <TrashIcon />
          </button>
        </div>
      </header>

      <nav className="mt-8 flex gap-1 border-b border-platinum">
        {(
          [
            ['info', 'General info'],
            ['opportunities', `Opportunities (${contact.opportunities.length})`],
            ['notes', 'Notes'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={
              'px-4 py-2 text-sm border-b-2 -mb-px transition-colors ' +
              (tab === k
                ? 'border-graphite text-graphite font-medium'
                : 'border-transparent text-ink-tertiary hover:text-ink')
            }
          >
            {label}
          </button>
        ))}
      </nav>

      <section className="mt-6">
        {tab === 'info' && <InfoTab contact={contact} />}
        {tab === 'opportunities' && (
          <OpportunitiesTab
            contact={contact}
            onCreate={() => setNewOpp(true)}
            navigate={navigate}
          />
        )}
        {tab === 'notes' && (
          <article className="card p-6 whitespace-pre-wrap text-sm text-ink leading-relaxed">
            {contact.notes ?? (
              <span className="italic text-ink-tertiary">No notes for this contact.</span>
            )}
          </article>
        )}
      </section>

      <NewOpportunityModal
        open={newOpp}
        contact={contact}
        onClose={() => setNewOpp(false)}
        onCreated={(opportunityId) => {
          setNewOpp(false);
          navigate(`/opportunities/${opportunityId}`);
        }}
      />

      <ContactFormModal
        open={editing}
        editContact={contact}
        onClose={() => setEditing(false)}
        onCreated={async (cid) => {
          setEditing(false);
          // Refresh the contact in-place so the detail view reflects edits.
          const refreshed = await apiClient.getContact(cid);
          setContact(refreshed);
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        variant="danger"
        title={`Delete ${contact.first_name} ${contact.last_name}?`}
        body={
          <>
            <p>
              This will permanently delete this contact from the directory.
              {contact.opportunities.length > 0 && (
                <>
                  {' '}
                  <strong className="text-ink">
                    {contact.opportunities.length} linked opportunit
                    {contact.opportunities.length === 1 ? 'y' : 'ies'}
                  </strong>{' '}
                  will be deleted at the same time.
                </>
              )}
            </p>
            <p className="mt-2 text-ink-tertiary">This cannot be undone.</p>
          </>
        }
        confirmLabel="Delete contact"
        busy={deleting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={performDelete}
      />
    </div>
  );
}

function InfoTab({ contact }: { contact: ContactRow }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Contact details">
        <Row label="Email">{contact.email_primary ?? '—'}</Row>
        {contact.email_secondary && <Row label="Email 2">{contact.email_secondary}</Row>}
        <Row label="Phone" mono>
          {contact.phone_primary ?? '—'}
        </Row>
        {contact.phone_secondary && (
          <Row label="Phone 2" mono>
            {contact.phone_secondary}
          </Row>
        )}
        {contact.linkedin_url && (
          <Row label="LinkedIn">
            <a
              href={contact.linkedin_url}
              target="_blank"
              rel="noreferrer"
              className="underline text-graphite"
            >
              {contact.linkedin_url}
            </a>
          </Row>
        )}
      </Card>

      <Card title="Addresses">
        {contact.addresses.length === 0 && (
          <p className="italic text-ink-tertiary text-sm">No address on file.</p>
        )}
        {contact.addresses.map((a) => (
          <div
            key={a.id ?? `${a.label}-${a.street ?? ''}`}
            className="border-t border-platinum first:border-t-0 first:pt-0 pt-3 mt-3"
          >
            <p className="label-uppercase">— {a.label}</p>
            <p className="mt-1 text-sm text-ink">
              {a.street || '—'}
              <br />
              <span className="font-tabular">{a.postal_code ?? ''}</span> {a.city ?? ''}{' '}
              {a.country && <span className="text-ink-tertiary">· {a.country}</span>}
            </p>
          </div>
        ))}
      </Card>

      <Card title="Wealth">
        <Row label="Tranche">{WEALTH_TRANCHE_LABEL[contact.wealth_tranche]}</Row>
        <Row label="Source">{WEALTH_SOURCE_LABEL[contact.wealth_source]}</Row>
        <Row label="Tax residence" mono>
          {contact.tax_residence ?? '—'}
        </Row>
        {contact.nationalities.length > 0 && (
          <Row label="Nationalities" mono>
            {contact.nationalities.join(' · ')}
          </Row>
        )}
      </Card>

      <Card title="KYC">
        <Row label="Status">{contact.kyc_status}</Row>
        <Row label="PEP">{contact.pep_flag ? 'Yes' : 'No'}</Row>
        {contact.id_document_type && (
          <Row label={`${contact.id_document_type}`} mono>
            {contact.id_document_masked ?? '—'}
            {contact.id_document_expiry && (
              <span className="block text-xs text-ink-tertiary">
                expires {contact.id_document_expiry}
              </span>
            )}
          </Row>
        )}
        <Row label="Last review" mono>
          {contact.kyc_last_review_date ?? '—'}
        </Row>
      </Card>
    </div>
  );
}

function OpportunitiesTab({
  contact,
  onCreate,
  navigate,
}: {
  contact: ContactRow;
  onCreate: () => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  if (contact.opportunities.length === 0) {
    return (
      <article className="card p-8 text-center">
        <p className="text-sm text-ink-tertiary">No linked opportunities yet.</p>
        <button type="button" onClick={onCreate} className="btn-primary mt-4">
          + Create the first opportunity
        </button>
      </article>
    );
  }
  return (
    <ul className="card divide-y divide-platinum">
      {contact.opportunities.map((o) => (
        <li
          key={o.id}
          onClick={() => navigate(`/opportunities/${o.id}`)}
          className="px-5 py-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-glacier/50 transition-colors"
        >
          <div>
            <p className="font-medium text-ink">{o.name}</p>
            <p className="text-xs text-ink-tertiary uppercase tracking-wider mt-1">
              {STAGE_LABEL[o.stage] ?? o.stage}
            </p>
          </div>
          <div className="text-right">
            <p className="font-tabular text-sm text-ink">{formatChf(o.score_chf)}</p>
            <p className="text-xs font-tabular text-ink-tertiary">
              Init {formatChf(o.initial_expected_amount)}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <article className="card p-5">
      <p className="label-uppercase">— {title}</p>
      <div className="mt-3 space-y-2">{children}</div>
    </article>
  );
}

function Row({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-3 gap-3 text-sm">
      <span className="text-ink-tertiary uppercase text-[11px] tracking-wider self-start pt-0.5">
        {label}
      </span>
      <span className={'col-span-2 text-ink ' + (mono ? 'font-tabular' : '')}>{children}</span>
    </div>
  );
}
